import React from 'react';
import PropTypes from 'prop-types';

import './historyDrawer.css';

import { epochToLocalDate } from '../../utils/dateConverter';

// Shared right-side drawer for a status transition log.
//
// Extracted from PayloadNew's StatusDrawer so the ticket banner can mount the
// same component from Display, which never loads payloadNew.css. Classes are
// `.ds-*` per DESIGN_SYSTEM.md §0.4 and the stylesheet travels with the
// component instead of living in a page's sheet.
//
// Every entry is a TRANSITION, not a state: `current_status` is where it moved
// to and `prev_status` where it came from. The payload log and the ticket log
// share that shape — which is the whole reason one component serves both — and
// neither carries a `status` field, so reading one yields undefined.
//
// The caller owns the vocabulary: `statusDesc` turns a code into text, and the
// header strings arrive already translated. That keeps the enum of each domain
// out of here.

const CheckMark = () => (
	<svg width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='currentColor'
		strokeWidth='3' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
		<path d='M20 6 9 17l-5-5' />
	</svg>
);

const CloseMark = () => (
	<svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor'
		strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
		<path d='M6 6l12 12M18 6 6 18' />
	</svg>
);

// `user` is a plain string in the payload log and an unpopulated `{$oid}` in the
// ticket one, so anything that isn't a string is dropped rather than rendered as
// "[object Object]". Callers that know better pass their own resolver.
const defaultAuthor = (entry) => (typeof entry?.user === 'string' ? `by ${entry.user}` : null);

const HistoryDrawer = ({
	open,
	onClose,
	history,
	statusDesc,
	eyebrow,
	title,
	emptyText,
	fromLabel,
	closeLabel,
	author,
}) => {
	// Newest first: the drawer answers "what happened last". The API hands the
	// log in chronological order, so the caller passes it through untouched.
	const reversed = Array.isArray(history) ? [...history].reverse() : [];

	return (
		<>
			<div
				className={`ds-drawer-backdrop${open ? ' open' : ''}`}
				onClick={onClose}
			/>
			<aside className={`ds-drawer${open ? ' open' : ''}`} aria-hidden={!open}>
				<div className='ds-drawer-head'>
					<div>
						{eyebrow && <div className='ds-drawer-eyebrow'>{eyebrow}</div>}
						<div className='ds-drawer-title'>{title}</div>
					</div>
					<button
						type='button'
						className='ds-drawer-close'
						onClick={onClose}
						aria-label={closeLabel}
					>
						<CloseMark />
					</button>
				</div>
				<div className='ds-drawer-body'>
					{reversed.length === 0 ? (
						<p className='ds-drawer-empty'>{emptyText}</p>
					) : (
						<div className='ds-timeline'>
							<div className='ds-timeline-rail' />
							{reversed.map((h, i) => {
								const isLatest = i === 0;
								const date = h?.date?.$date
									? epochToLocalDate(h.date.$date, localStorage.getItem('i18nextLng'))
									: '';
								const stateCode = h?.current_status ?? h?.status;
								const stateLabel = typeof stateCode === 'number'
									? statusDesc(stateCode)
									: (h?.state ?? '—');
								const prevCode = h?.prev_status;
								const prevLabel = typeof prevCode === 'number' ? statusDesc(prevCode) : null;
								// Optional across the whole API: most transitions carry none.
								const comment = typeof h?.comment === 'string' ? h.comment.trim() : '';
								const by = author(h);

								return (
									<div className='ds-timeline-item' key={i}>
										<div className={`ds-timeline-dot${isLatest ? ' latest' : ''}`}>
											{isLatest && <CheckMark />}
										</div>
										<div className='ds-timeline-row'>
											<div className='ds-timeline-state'>{stateLabel}</div>
											<div className='ds-timeline-date'>{date}</div>
										</div>
										{prevLabel && (
											<div className='ds-timeline-note'>
												{fromLabel ? `${fromLabel} ` : ''}
												<strong>{prevLabel}</strong> → <strong>{stateLabel}</strong>
											</div>
										)}
										{comment && <p className='ds-timeline-comment'>{comment}</p>}
										{by && <div className='ds-timeline-user'>{by}</div>}
									</div>
								);
							})}
						</div>
					)}
				</div>
			</aside>
		</>
	);
};

HistoryDrawer.propTypes = {
	open: PropTypes.bool,
	onClose: PropTypes.func.isRequired,
	history: PropTypes.array,
	statusDesc: PropTypes.func.isRequired,
	eyebrow: PropTypes.string,
	title: PropTypes.string.isRequired,
	emptyText: PropTypes.string,
	fromLabel: PropTypes.string,
	closeLabel: PropTypes.string,
	author: PropTypes.func,
};

HistoryDrawer.defaultProps = {
	open: false,
	history: [],
	eyebrow: '',
	emptyText: 'No history available.',
	fromLabel: 'From',
	closeLabel: 'Close',
	author: defaultAuthor,
};

export default HistoryDrawer;
